<script setup lang="ts">
import type { Presentation } from '~/models/Presentation/Presentation'
import type { PresentationPage } from '~/models/Presentation/BasePage'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { faArrowLeft, faArrowRight } from '@fortawesome/duotone-regular-svg-icons'
import useAuthStore from '~/stores/auth'
import { PendoFeature } from '~/models/Pendo'
import Button from '~/components/ui/Button.vue'
import NavigationPopover from '~/components/PresentationPages/Components/NavigationPopover.vue'

const props = defineProps<{
  pageNumber: number
  pages: PresentationPage[]
  presentation: Presentation
}>()

const emit = defineEmits(['nextPage', 'prevPage'])

const { t } = useI18n()
const { isAuthenticated } = storeToRefs(useAuthStore())

const totalPages = computed(() => props.pages.length)
const multiplePages = computed(() => props.pages.length > 1)
const canGoToNextPage = computed(() => (props.pageNumber < totalPages.value) && isAuthenticated.value)
const canGoToPreviousPage = computed(() => (props.pageNumber > 1) && isAuthenticated.value)
const currentPage = computed(() => props.pages[props.pageNumber - 1])
const colorPair = computed(() => currentPage.value.colorPair)
</script>

<template>
  <div
    class="pointer-events-none absolute inset-x-0 bottom-0 flex flex-col"
    data-ignore-tts
    :style="{ 'color': colorPair.text, '--hover-bg': colorPair.text, '--hover-color': colorPair.background }"
  >
    <div class="flex items-center justify-between gap-4 p-4">
      <div class="pointer-events-auto flex items-center gap-4">
        <div id="slide-action" class="empty:hidden" />
      </div>

      <div class="pointer-events-auto flex items-center gap-4">
        <Button
          v-if="multiplePages"
          :disabled="!canGoToPreviousPage"
          shape="square"
          size="xl"
          :aria-label="t('search.paginator.prevPage')"
          variant="outline"
          :style="{ background: colorPair.background, color: colorPair.text, outlineColor: colorPair.text }"
          class="hover:not-disabled:!bg-(--hover-bg) hover:not-disabled:!text-(--hover-color)"
          :data-pendo="PendoFeature.Presentation.Previous"
          @click="emit('prevPage')"
        >
          <font-awesome-icon :icon="faArrowLeft" />
        </Button>
        <NavigationPopover
          v-if="multiplePages"
          :key="`${presentation.locationId}-${pageNumber}`"
          :presentation="presentation"
          :page-number="pageNumber"
          :pages="pages"
        />
        <Button
          v-if="multiplePages"
          :disabled="!canGoToNextPage"
          shape="square"
          size="xl"
          :aria-label="t('search.paginator.nextPage')"
          variant="outline"
          :style="{ background: colorPair.background, color: colorPair.text, outlineColor: colorPair.text }"
          class="hover:not-disabled:!bg-(--hover-bg) hover:not-disabled:!text-(--hover-color)"
          :data-pendo="PendoFeature.Presentation.Next"
          @click="emit('nextPage')"
        >
          <font-awesome-icon :icon="faArrowRight" />
        </Button>
      </div>
    </div>
    <div
      id="audio-embed-outlet"
      class="pointer-events-auto [&:not(:empty)]:p-4"
      :style="{ backgroundColor: colorPair.text, color: colorPair.background }"
    />
  </div>
</template>
